import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const ProjectsPreview = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', label: 'All Projects', icon: 'Grid3X3' },
    { id: 'cleaning', label: 'Cleaning', icon: 'Sparkles' },
    { id: 'flooring', label: 'Flooring', icon: 'Square' },
    { id: 'commercial', label: 'Commercial', icon: 'Building' }
  ];

  const projects = [
    {
      id: 1,
      title: "Luxury Home Deep Clean",
      category: "cleaning",
      type: "Residential Deep Cleaning",
      location: "Beverly Hills",
      beforeImage: "https://images.pexels.com/photos/4239013/pexels-photo-4239013.jpeg?auto=compress&cs=tinysrgb&w=600",
      afterImage: "https://images.pexels.com/photos/4239091/pexels-photo-4239091.jpeg?auto=compress&cs=tinysrgb&w=600",
      description: "Complete transformation of a 4-bedroom luxury home with deep cleaning, carpet treatment, and sanitization.",
      duration: "2 days",
      size: "3,500 sq ft"
    },
    {
      id: 2,
      title: "Corporate Office Renovation",
      category: "commercial",
      type: "Post-Construction Cleaning",
      location: "Downtown",
      beforeImage: "https://images.pixabay.com/photo/2016/11/29/03/53/architecture-1867187_1280.jpg",
      afterImage: "https://images.pixabay.com/photo/2017/03/28/12/11/chairs-2181947_1280.jpg",
      description: "Post-construction cleanup and final detailing for a modern corporate office space.",
      duration: "3 days",
      size: "5,000 sq ft"
    },
    {
      id: 3,
      title: "Hardwood Floor Restoration",
      category: "flooring",
      type: "Floor Refinishing",
      location: "Suburban Home",
      beforeImage: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=600&q=80",
      afterImage: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?auto=format&fit=crop&w=600&q=80",
      description: "Complete hardwood floor restoration including sanding, staining, and protective coating application.",
      duration: "4 days",
      size: "1,200 sq ft"
    },
    {
      id: 4,
      title: "Restaurant Kitchen Deep Clean",
      category: "commercial",
      type: "Commercial Kitchen Cleaning",
      location: "Food District",
      beforeImage: "https://images.pexels.com/photos/2696064/pexels-photo-2696064.jpeg?auto=compress&cs=tinysrgb&w=600",
      afterImage: "https://images.pexels.com/photos/2696064/pexels-photo-2696064.jpeg?auto=compress&cs=tinysrgb&w=600",
      description: "Comprehensive kitchen cleaning and sanitization to meet health department standards.",
      duration: "1 day",
      size: "800 sq ft"
    },
    {
      id: 5,
      title: "Vinyl Flooring Installation",
      category: "flooring",
      type: "New Floor Installation",
      location: "Modern Apartment",
      beforeImage: "https://images.pixabay.com/photo/2016/11/18/17/20/living-room-1835923_1280.jpg",
      afterImage: "https://images.pixabay.com/photo/2017/03/28/12/11/chairs-2181947_1280.jpg",
      description: "Premium vinyl plank flooring installation with moisture barrier and trim work.",
      duration: "2 days",
      size: "900 sq ft"
    },
    {
      id: 6,
      title: "Carpet Stain Removal",
      category: "cleaning",
      type: "Specialized Carpet Treatment",
      location: "Family Home",
      beforeImage: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=600&q=80",
      afterImage: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?auto=format&fit=crop&w=600&q=80",
      description: "Professional pet stain and odor removal using advanced cleaning techniques and equipment.",
      duration: "1 day",
      size: "Living areas"
    }
  ];

  const filteredProjects = activeCategory === 'all' 
    ? projects 
    : projects?.filter(project => project?.category === activeCategory);

  const [hoveredProject, setHoveredProject] = useState(null);

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4 lg:px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary mb-6">
            Our Recent Projects
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto mb-8">
            See the incredible transformations we've achieved for our clients. 
            From deep cleaning to flooring installations, every project tells a story of excellence.
          </p>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-2 lg:gap-4">
            {categories?.map((category) => (
              <button
                key={category?.id}
                onClick={() => setActiveCategory(category?.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  activeCategory === category?.id
                    ? 'bg-primary text-white shadow-elevation-1'
                    : 'bg-card text-text-secondary hover:text-text-primary hover:bg-muted border border-border'
                }`}
              >
                <Icon name={category?.icon} size={16} />
                <span>{category?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects?.map((project) => (
            <div
              key={project?.id}
              className="group bg-card rounded-xl overflow-hidden shadow-elevation-1 hover:shadow-elevation-3 transition-all duration-300"
              onMouseEnter={() => setHoveredProject(project?.id)}
              onMouseLeave={() => setHoveredProject(null)}
            >
              {/* Before/After Images */}
              <div className="relative h-64 overflow-hidden">
                <div className="flex h-full">
                  <div className="w-1/2 relative">
                    <Image
                      src={project?.beforeImage}
                      alt={`${project?.title} - Before`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                      Before
                    </div>
                  </div>
                  <div className="w-1/2 relative">
                    <Image
                      src={project?.afterImage}
                      alt={`${project?.title} - After`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-2 right-2 bg-primary text-white text-xs px-2 py-1 rounded">
                      After
                    </div>
                  </div>
                </div>

                {/* Hover Overlay */}
                <div className={`absolute inset-0 bg-black/60 flex items-center justify-center transition-opacity duration-300 ${
                  hoveredProject === project?.id ? 'opacity-100' : 'opacity-0'
                }`}>
                  <Button
                    variant="default"
                    iconName="Eye"
                    iconPosition="left"
                    className="transform scale-90 group-hover:scale-100 transition-transform duration-200"
                  >
                    View Details
                  </Button>
                </div>
              </div>

              {/* Project Info */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-primary uppercase tracking-wide">
                    {project?.type}
                  </span>
                  <div className="flex items-center text-xs text-text-secondary">
                    <Icon name="MapPin" size={12} className="mr-1" />
                    {project?.location}
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-text-primary mb-3">
                  {project?.title}
                </h3>

                <p className="text-text-secondary text-sm mb-4 leading-relaxed">
                  {project?.description}
                </p>

                {/* Project Stats */}
                <div className="flex items-center justify-between text-xs text-text-secondary mb-4">
                  <div className="flex items-center">
                    <Icon name="Clock" size={12} className="mr-1" />
                    {project?.duration}
                  </div>
                  <div className="flex items-center">
                    <Icon name="Square" size={12} className="mr-1" />
                    {project?.size}
                  </div>
                </div>

                {/* View Project Button */}
                <Link to="/projects-gallery" className="block">
                  <Button
                    variant="outline"
                    fullWidth
                    iconName="ArrowRight"
                    iconPosition="right"
                    className="group-hover:bg-primary group-hover:text-white group-hover:border-primary"
                  >
                    View Project
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <p className="text-text-secondary mb-6">
            Want to see more of our work? Browse our complete project gallery.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/projects-gallery">
              <Button
                variant="default"
                size="lg"
                iconName="Image"
                iconPosition="left"
              >
                View All Projects
              </Button>
            </Link>
            <Link to="/smart-contact">
              <Button
                variant="outline"
                size="lg"
                iconName="Calendar"
                iconPosition="left"
              >
                Start Your Project
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectsPreview;